import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Eye, EyeOff, KeyRound, Loader2, Mail, Plus, Search, Shield, Trash2, UserPlus, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import Layout from "../../components/Layout";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { Separator } from "../../components/ui/separator";

export default function GestioneUtenti() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");

  // Detail dialog state
  const [detailUser, setDetailUser] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [newPwd, setNewPwd] = useState("");
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
  }, [users, search]);

  const onCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/users", form);
      toast.success("Utente giudice creato");
      setOpenCreate(false);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("Utente eliminato");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openDetail = async (id) => {
    setDetailLoading(true);
    setShowPwd(false);
    setNewPwd("");
    try {
      const { data } = await api.get(`/users/${id}`);
      setDetailUser(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDetailLoading(false);
    }
  };

  const onResetPassword = async () => {
    if (!newPwd || newPwd.length < 4) {
      toast.error("La password deve avere almeno 4 caratteri");
      return;
    }
    setResetting(true);
    try {
      await api.post(`/users/${detailUser.id}/reset-password`, { new_password: newPwd });
      toast.success("Password aggiornata");
      // Reload detail
      const { data } = await api.get(`/users/${detailUser.id}`);
      setDetailUser(data);
      setNewPwd("");
      setShowPwd(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setResetting(false);
    }
  };

  return (
    <Layout>
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="back-to-admin">
        <ArrowLeft className="h-4 w-4" /> Torna al pannello
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-blue-700 font-bold">Amministrazione</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-2">Gestione Utenti Giudici</h1>
          <p className="text-slate-500 mt-2">Crea le utenze e gestisci i profili dei responsabili / giudici.</p>
        </div>

        <Dialog open={openCreate} onOpenChange={setOpenCreate}>
          <DialogTrigger asChild>
            <Button className="bg-blue-700 hover:bg-blue-800 text-white h-11 px-5" data-testid="open-create-user">
              <Plus className="h-4 w-4 mr-2" /> Nuovo Utente
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2"><UserPlus className="h-5 w-5 text-blue-700" /> Nuovo utente giudice</DialogTitle>
              <DialogDescription>Inserisci i dati del nuovo giudice/responsabile.</DialogDescription>
            </DialogHeader>
            <form onSubmit={onCreate} className="space-y-4">
              <div>
                <Label className="text-sm font-semibold text-slate-700">Nome e Cognome *</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-2 h-11" placeholder="MARIO ROSSI" data-testid="new-user-name" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700">Email *</Label>
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-2 h-11" placeholder="mario.rossi@esempio.it" data-testid="new-user-email" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700">Password *</Label>
                <Input required type="text" minLength={4} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="mt-2 h-11" data-testid="new-user-password" />
              </div>
              <DialogFooter className="gap-2 mt-2">
                <Button type="button" variant="outline" onClick={() => setOpenCreate(false)} data-testid="cancel-create-user">Annulla</Button>
                <Button type="submit" disabled={submitting} className="bg-blue-700 hover:bg-blue-800" data-testid="submit-create-user">
                  {submitting ? "Creazione…" : "Crea utente"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="mb-5">
        <div className="relative max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            placeholder="Cerca per nome o email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-11 bg-white"
            data-testid="users-search"
          />
        </div>
      </div>

      <div className="bg-white rounded-2xl ring-1 ring-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-display font-semibold text-slate-900 flex items-center gap-2">
            <Users className="h-5 w-5 text-slate-400" /> Utenze
          </h2>
          <span className="text-xs text-slate-500" data-testid="users-count">
            {filtered.length === users.length ? `${users.length} utenti` : `${filtered.length} di ${users.length}`}
          </span>
        </div>

        {loading ? (
          <div className="p-10 text-center text-slate-500 text-sm">Caricamento…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">
            {users.length === 0 ? "Nessun utente creato." : "Nessun utente corrisponde alla ricerca."}
          </div>
        ) : (
          <div className="max-h-[640px] overflow-y-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0">
                <tr>
                  <th className="px-6 py-3 font-semibold">Nome</th>
                  <th className="px-6 py-3 font-semibold">Email</th>
                  <th className="px-6 py-3 font-semibold w-32">Creato il</th>
                  <th className="px-6 py-3 font-semibold w-32 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`user-row-${u.id}`}>
                    <td className="px-6 py-3.5">
                      <button
                        onClick={() => openDetail(u.id)}
                        className="font-medium text-slate-900 hover:text-blue-700 hover:underline text-left"
                        data-testid={`open-user-detail-${u.id}`}
                      >
                        {u.name}
                      </button>
                    </td>
                    <td className="px-6 py-3.5 text-slate-600 text-sm">{u.email}</td>
                    <td className="px-6 py-3.5 text-slate-500 text-sm">{new Date(u.created_at).toLocaleDateString("it-IT")}</td>
                    <td className="px-6 py-3.5 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-blue-700 hover:bg-blue-50 hover:text-blue-800"
                          onClick={() => openDetail(u.id)}
                          data-testid={`view-user-${u.id}`}
                        >
                          <Shield className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700" data-testid={`delete-user-${u.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Eliminare {u.name}?</AlertDialogTitle>
                              <AlertDialogDescription>L'azione non può essere annullata.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annulla</AlertDialogCancel>
                              <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={() => onDelete(u.id)} data-testid={`confirm-delete-user-${u.id}`}>
                                Elimina
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detailUser || detailLoading} onOpenChange={(o) => { if (!o) { setDetailUser(null); setNewPwd(""); setShowPwd(false); } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-700" /> Dettaglio profilo
            </DialogTitle>
            <DialogDescription>Informazioni complete dell'utente.</DialogDescription>
          </DialogHeader>

          {detailLoading || !detailUser ? (
            <div className="p-10 text-center text-slate-500 text-sm">Caricamento…</div>
          ) : (
            <div className="space-y-4" data-testid="user-detail-panel">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Nome e Cognome</Label>
                <div className="mt-1 font-display text-lg font-semibold text-slate-900" data-testid="detail-name">{detailUser.name}</div>
              </div>
              <Separator />
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Email (Login)</Label>
                <div className="mt-1 text-slate-800 inline-flex items-center gap-2" data-testid="detail-email">
                  <Mail className="h-4 w-4 text-slate-400" /> {detailUser.email}
                </div>
              </div>
              <Separator />
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Password</Label>
                <div className="mt-1 flex items-center gap-2">
                  <code className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono text-slate-900 select-all" data-testid="detail-password">
                    {detailUser.plain_password
                      ? (showPwd ? detailUser.plain_password : "•".repeat(Math.max(detailUser.plain_password.length, 8)))
                      : <span className="italic text-slate-400">Non disponibile</span>}
                  </code>
                  {detailUser.plain_password && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowPwd((v) => !v)}
                      data-testid="toggle-show-password"
                    >
                      {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  )}
                </div>
              </div>
              <Separator />
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Creato il</Label>
                <div className="mt-1 text-slate-700 text-sm" data-testid="detail-created">
                  {new Date(detailUser.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-2">
                <div className="flex items-center gap-2 mb-3">
                  <KeyRound className="h-4 w-4 text-amber-700" />
                  <span className="font-display font-semibold text-amber-900">Reimposta password</span>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    placeholder="Nuova password"
                    value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)}
                    className="flex-1 h-10 bg-white"
                    data-testid="reset-password-input"
                  />
                  <Button
                    onClick={onResetPassword}
                    disabled={resetting || !newPwd}
                    className="bg-amber-600 hover:bg-amber-700 text-white h-10"
                    data-testid="reset-password-button"
                  >
                    {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reimposta"}
                  </Button>
                </div>
                <p className="text-xs text-amber-800 mt-2">La nuova password sostituirà quella precedente.</p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setDetailUser(null); setNewPwd(""); }} data-testid="close-user-detail">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
